import { GcpDropDownItem } from '@back-office/ui';
import { finance } from 'faker';
import { useLocalStore } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import { useSnackbar } from 'notistack';
import qs from 'query-string';
import React, { useCallback } from 'react';
import {
  Control,
  DeepMap,
  FieldError,
  FieldValues,
  FormState,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  AUTO_GENERATED_REF1_LENGTH,
  excludedTerminalId,
  STATUS_CODE_SUCCESS,
} from '../../../constants';
import { CreateQRForm } from '../../../forms';
import useInject from '../../../hooks/useInject';
import AuthModel from '../../../stores/auth.store';

type UseViewModelReturn = {
  control: Control<CreateQRForm>;
  handleSubmit: <TSubmitFieldValues extends FieldValues = CreateQRForm>(
    onValid: SubmitHandler<TSubmitFieldValues>,
    onInvalid?: SubmitErrorHandler<CreateQRForm> | undefined
  ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
  clearErrors: (
    name?: keyof CreateQRForm | (keyof CreateQRForm)[] | undefined
  ) => void;
  errors: DeepMap<CreateQRForm, FieldError>;
  formState: FormState<CreateQRForm>;
  state: {
    v4: string;
    acqMerchantId: string;
    readonly ref1: string;
    readonly data: GcpDropDownItem[];
  };
  handleOnChange: (value: string, length: number) => string;
  handleOnSubmit: (data: CreateQRForm) => Promise<void>;
  auth: Instance<typeof AuthModel>;
};

const useViewModel = (): UseViewModelReturn => {
  const { control, handleSubmit, clearErrors, errors, formState } =
    useForm<CreateQRForm>({
      mode: 'onChange',
      reValidateMode: 'onSubmit',
    });
  const { getInvoice, auth, invoice } = useInject((root) => root);

  const { t } = useTranslation();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const state = useLocalStore(() => ({
    v4: finance.account(10),
    acqMerchantId: auth.currentUser.mid,
    get ref1(): string {
      return `${this.acqMerchantId}${state.v4.substring(
        0,
        AUTO_GENERATED_REF1_LENGTH - this.acqMerchantId.length
      )}`;
    },
    get data(): GcpDropDownItem[] {
      const dropdownData = auth.currentUser?.requesters?.reduce<
        GcpDropDownItem[]
      >((items, currentItem) => {
        if (!excludedTerminalId.includes(currentItem.tid)) {
          items.push({
            label: `${currentItem.tid} - ${currentItem.address1}`,
            value: currentItem.tid,
          });
        }
        return items;
      }, []);
      return dropdownData || [{ label: 'No Terminal ID', value: null }];
    },
  }));

  const handleOnChange = useCallback(
    (value: string, length: number): string =>
      value.trim().substring(0, length),
    []
  );

  const handleOnSubmit = async (data: CreateQRForm): Promise<void> => {
    const tidDetail = auth.currentUser.requesters.find(
      (requester) => requester.tid === data.acqRequesterId
    );
    try {
      const response = await getInvoice({
        acqMerchantId: state.acqMerchantId,
        ...data,
        amount: data.amount.replace(',', ''),
      });
      const { status } = response;
      if (status.code === STATUS_CODE_SUCCESS) {
        const query = qs.stringify({
          txId: invoice.txId,
          detail: encodeURIComponent(
            tidDetail ? `${tidDetail.tid} - ${tidDetail.address1}` : '-'
          ),
        });
        history.push(`/generated_qr?${query}`);
      }
    } catch (e) {
      enqueueSnackbar(t('dialog.failed_create_qr'), {
        variant: 'error',
      });
    }
  };

  return {
    control,
    handleSubmit,
    clearErrors,
    errors,
    formState,
    state,
    handleOnChange,
    handleOnSubmit,
    auth,
  };
};

export default useViewModel;
